import React, { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, X, Download, Calculator, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { addExpenses } from '../../Fetch/fetchExpenses';
import styles from './ExpenseExcelUpload.module.css';
import * as XLSX from 'xlsx';

const ExpenseExcelUpload = ({ onClose, onExpensesCreated, buildingId }) => {
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]); // Store all raw data
  const [excelData, setExcelData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0); // New state for header row selection
  const [columnMapping, setColumnMapping] = useState({
    date: '',
    description: '',
    amount: '',
    category: '',
    vendor: '',
    invoiceNumber: '',
    paidTo: '',
    paymentMethod: '' // Added paymentMethod
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ success: [], errors: [] });
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Header Selection, 3: Mapping, 4: Processing, 5: Results

  // Categories for validation
  const categories = [
    'תחזוקה שוטפת',
    'תיקונים',
    'חשבונות',
    'ביטוח',
    'שיפוצים',
    'ניקיון', // Added missing category
    'אחר'
  ];

  // Payment methods for validation - NEW
  const paymentMethods = [
    'מזומן',
    'צ\'ק',
    'העברה בנקאית',
    'ביט'
  ];

  // Update headers and data when header row selection changes
  const updateHeadersAndData = useCallback((data, headerIndex) => {
    if (data.length <= headerIndex) return;

    const headerRow = data[headerIndex].map(header =>
      header ? String(header).trim() : ''
    );

    const dataRows = data.slice(headerIndex + 1)
      .filter(row => row && row.some(cell => cell !== undefined && cell !== null && cell !== ''))
      .map(row => row.map(cell =>
        cell !== undefined && cell !== null ? String(cell).trim() : ''
      ));

    setHeaders(headerRow);
    setExcelData(dataRows);
  }, []);

  // Handle header row selection change
  const handleHeaderRowChange = (newHeaderIndex) => {
    setHeaderRowIndex(newHeaderIndex);
    updateHeadersAndData(rawData, newHeaderIndex);
    // Reset column mapping when header row changes
    setColumnMapping({
      date: '',
      description: '',
      amount: '',
      category: '',
      vendor: '',
      invoiceNumber: '',
      paidTo: '',
      paymentMethod: '' // Reset paymentMethod
    });
  };

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.match(/\.(xlsx|xls|csv)$/)) {
      alert('אנא בחר קובץ Excel (.xlsx, .xls) או CSV (.csv)');
      return;
    }

    setFile(uploadedFile);

    try {
      let processedData = [];

      if (uploadedFile.name.match(/\.csv$/)) {
        // Handle CSV files
        const text = await uploadedFile.text();
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('הקובץ חייב להכיל לפחות שורת כותרת ושורת נתונים אחת');
          return;
        }

        processedData = lines.map(line =>
          line.split(',').map(cell => cell.trim().replace(/"/g, ''))
        ).filter(row => row.some(cell => cell !== ''));

      } else if (uploadedFile.name.match(/\.xlsx?$/)) {
        // Handle Excel files using xlsx library
        try {
          const arrayBuffer = await uploadedFile.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON array
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            alert('הקובץ חייב להכיל לפחות שורת כותרת ושורת נתונים אחת');
            return;
          }

          processedData = jsonData;

        } catch (error) {
          alert('שגיאה בקריאת קובץ ה-Excel. אנא בדוק שהקובץ תקין');
          return;
        }
      } else {
        alert('נא להעלות קובץ CSV או Excel בלבד');
        return;
      }

      // Store raw data and initialize with first row as header
      setRawData(processedData);
      setHeaderRowIndex(0);
      updateHeadersAndData(processedData, 0);
      setCurrentStep(2); // Go to header selection step

    } catch (error) {
      alert('שגיאה בקריאת הקובץ');
    }
  }, [updateHeadersAndData]);

  // Process ALL expenses
  const processExpenses = async () => {
    if (!columnMapping.description || !columnMapping.amount) {
      alert('חובה לבחור עמודות עבור תיאור וסכום');
      return;
    }

    // Helper function to safely parse dates
    const parseDateSafely = (dateValue) => {
      if (!dateValue || dateValue === null || dateValue === undefined || dateValue === '') {
        return { isValid: false, isoString: null };
      }

      const dateStr = String(dateValue).trim();
      if (dateStr === '' || dateStr === 'null' || dateStr === 'undefined' || dateStr === 'NULL') {
        return { isValid: false, isoString: null };
      }

      try {
        const parsed = new Date(dateStr);
        if (isNaN(parsed.getTime()) || parsed.getFullYear() < 1900) {
          return { isValid: false, isoString: null };
        }
        return { isValid: true, isoString: parsed.toISOString() };
      } catch (error) {
        return { isValid: false, isoString: null };
      }
    };

    setIsProcessing(true);
    setCurrentStep(4);
    setProgress({ current: 0, total: excelData.length });

    const successResults = [];
    const errorResults = [];


    // Process ALL rows
    for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
      const row = excelData[rowIndex];


      try {
        // Update progress
        setProgress({ current: rowIndex + 1, total: excelData.length });

        // Get column indexes
        const dateIndex = columnMapping.date ? headers.findIndex(h => h === columnMapping.date) : -1;
        const descriptionIndex = headers.findIndex(h => h === columnMapping.description);
        const amountIndex = headers.findIndex(h => h === columnMapping.amount);
        const categoryIndex = columnMapping.category ? headers.findIndex(h => h === columnMapping.category) : -1;
        const vendorIndex = columnMapping.vendor ? headers.findIndex(h => h === columnMapping.vendor) : -1;
        const invoiceNumberIndex = columnMapping.invoiceNumber ? headers.findIndex(h => h === columnMapping.invoiceNumber) : -1;
        const paidToIndex = columnMapping.paidTo ? headers.findIndex(h => h === columnMapping.paidTo) : -1;
        const paymentMethodIndex = columnMapping.paymentMethod ? headers.findIndex(h => h === columnMapping.paymentMethod) : -1;

        // Extract and clean values
        const rawDateValue = dateIndex >= 0 ? row[dateIndex] : null;
        const description = row[descriptionIndex] ? String(row[descriptionIndex]).trim() : '';
        const amountValue = row[amountIndex] ? String(row[amountIndex]).trim() : '';
        const category = categoryIndex >= 0 && row[categoryIndex] ? String(row[categoryIndex]).trim() : '';
        const vendor = vendorIndex >= 0 && row[vendorIndex] ? String(row[vendorIndex]).trim() : '';
        const invoiceNumber = invoiceNumberIndex >= 0 && row[invoiceNumberIndex] ? String(row[invoiceNumberIndex]).trim() : '';
        const paidTo = paidToIndex >= 0 && row[paidToIndex] ? String(row[paidToIndex]).trim() : '';
        const paymentMethod = paymentMethodIndex >= 0 && row[paymentMethodIndex] ? String(row[paymentMethodIndex]).trim() : '';

        // Validate required fields
        if (!description || description.length === 0) {
          throw new Error('תיאור חסר');
        }

        if (!amountValue || isNaN(parseFloat(amountValue)) || parseFloat(amountValue) <= 0) {
          throw new Error(`סכום לא תקין: "${amountValue}"`);
        }

        // Process date - Handle Excel date formats properly
        let validDateToSend = null;
        let shouldIncludeDate = false;

        console.log('Processing date for row', rowIndex + 1, '- rawDateValue:', rawDateValue, 'type:', typeof rawDateValue);

        // Only process if we have something that's not null/undefined/empty
        if (rawDateValue !== null && rawDateValue !== undefined && rawDateValue !== '') {
          const dateString = String(rawDateValue).trim();

          // Only proceed if we have a non-empty string that's not "null" or "undefined"
          if (dateString.length > 0 && dateString !== 'null' && dateString !== 'undefined' && dateString !== 'NULL') {
            try {
              let parsedDate;
              
              // Check if it's an Excel serial date number (typically a number > 1000)
              const numericValue = parseFloat(dateString);
              if (!isNaN(numericValue) && numericValue > 1000 && numericValue < 100000) {
                // Convert Excel serial date to JavaScript Date
                // Excel dates start from January 1, 1900 (with some adjustments)
                const excelEpoch = new Date(1900, 0, 1);
                parsedDate = new Date(excelEpoch.getTime() + (numericValue - 1) * 24 * 60 * 60 * 1000);
              } else {
                // Try parsing as regular date string
                parsedDate = new Date(dateString);
              }

              console.log('Parsed date:', parsedDate, 'year:', parsedDate.getFullYear());

              if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() >= 1900 && parsedDate.getFullYear() <= 2100) {
                validDateToSend = parsedDate.toISOString();
                shouldIncludeDate = true;
                console.log('Date accepted:', validDateToSend);
              } else {
                console.log('Date rejected - invalid year or NaN');
              }
            } catch (dateError) {
              console.log('Date parsing error:', dateError);
            }
          } else {
            console.log('Date string empty or invalid');
          }
        } else {
          console.log('Raw date value is null/undefined/empty');
        }

        // Process category
        let finalCategory = 'תחזוקה שוטפת';
        if (category && category.length > 0) {
          finalCategory = categories.includes(category) ? category : 'אחר';
        }

        // Process payment method
        let finalPaymentMethod = 'מזומן';
        if (paymentMethod && paymentMethod.length > 0) {
          finalPaymentMethod = paymentMethods.includes(paymentMethod) ? paymentMethod : 'מזומן';
        }

        // Parse amount
        const finalAmount = parseFloat(amountValue);

        // Create the expense object matching the API structure from Expenses.jsx
        const expenseData = {
          BuildingID: parseInt(buildingId),
          Description: description,
          Amount: finalAmount,
          Category: finalCategory,
          ExpenseType: 'קבועה',          // Use Hebrew like in Expenses.jsx
          PaymentMethod: finalPaymentMethod,
          PaidTo: paidTo || '',          // Always include PaidTo, empty string if not provided
          Vendor: vendor || '',          // Always include Vendor, empty string if not provided
          InvoiceNumber: invoiceNumber || '', // Always include InvoiceNumber, empty string if not provided
          ReceiptImageURL: '',           // Empty string instead of 'none'
          SupplierID: null,              // Set to null like in Expenses.jsx
          InvoiceImage: ''               // Empty string for Excel uploads (no image)
        };

        // CRITICAL: Only add Date if we have a valid date AND shouldIncludeDate is true
        if (shouldIncludeDate && validDateToSend) {
          // Double-check the date is valid before sending
          const testDate = new Date(validDateToSend);
          if (!isNaN(testDate.getTime()) && testDate.getFullYear() >= 1900 && testDate.getFullYear() <= 2100) {
            expenseData.Date = validDateToSend;
            console.log('Final date being sent:', validDateToSend);
          } else {
            console.log('Final date validation failed, setting to null');
            expenseData.Date = null;
          }
        } else {
          expenseData.Date = null;       // Explicitly set to null if no date
          console.log('No valid date available, setting to null');
        }


        // Basic validation - ensure required fields are not empty
        if (!expenseData.Description || expenseData.Description.trim() === '') {
          throw new Error('תיאור הוא שדה חובה');
        }
        if (!expenseData.Amount || isNaN(expenseData.Amount) || expenseData.Amount <= 0) {
          throw new Error('סכום חייב להיות מספר חיובי');
        }
        if (!expenseData.BuildingID || isNaN(expenseData.BuildingID)) {
          throw new Error('מזהה בניין חסר');
        }


        // Call the API with the validated data
        const result = await addExpenses(expenseData);

        // Add to success results
        successResults.push({
          row: rowIndex + 1,
          description: description,
          amount: finalAmount,
          category: finalCategory,
          paymentMethod: finalPaymentMethod,
          vendor: vendor || 'לא צוין'
        });


      } catch (error) {

        // Add to error results
        errorResults.push({
          row: rowIndex + 1,
          description: row[headers.findIndex(h => h === columnMapping.description)] || 'לא זמין',
          amount: row[headers.findIndex(h => h === columnMapping.amount)] || 'לא זמין',
          error: error.message
        });
      }

      // Small delay to prevent overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Update results
    setResults({ success: successResults, errors: errorResults });

    setIsProcessing(false);
    setCurrentStep(5);

    // Call the callback to refresh the expenses table if any expenses were created successfully
    if (successResults.length > 0 && onExpensesCreated) {
      onExpensesCreated();
    }

  };

  // Download template
  const downloadTemplate = () => {
    const csvContent = [
      ['תיאור', 'סכום', 'תאריך', 'קטגוריה', 'אמצעי תשלום', 'ספק', 'מספר חשבונית', 'תשלום ל'], // Reordered to put payment method earlier
      ['תיקון מעלית', '1500', '2024-01-15', 'תחזוקה שוטפת', 'העברה בנקאית', 'חברת מעליות אבג', 'INV-001', 'חברת מעליות אבג'],
      ['חשבון חשמל', '800', '2024-01-16', 'חשבונות', 'מזומן', 'חברת החשמל', 'ELE-12345', 'חברת החשמל'],
      ['שיפוץ חדר מדרגות', '3200', '2024-01-17', 'שיפוצים', 'ביט', 'בניין ושיפוצים בעמ', 'REF-789', 'בניין ושיפוצים בעמ'],
      ['ניקיון חדר מדרגות', '450', '2024-01-18', 'ניקיון', 'מזומן', 'חברת ניקיון', 'CLN-123', 'חברת ניקיון']
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_expenses.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetUpload = () => {
    setFile(null);
    setRawData([]);
    setExcelData([]);
    setHeaders([]);
    setHeaderRowIndex(0);
    setColumnMapping({
      date: '',
      description: '',
      amount: '',
      category: '',
      vendor: '',
      invoiceNumber: '',
      paidTo: '',
      paymentMethod: '' // Reset paymentMethod
    });
    setCurrentStep(1);
    setResults({ success: [], errors: [] });
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>
            <FileSpreadsheet size={24} />
            העלאת הוצאות מקובץ Excel או CSV
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Step 1: File Upload */}
        {currentStep === 1 && (
          <div className={styles.content}>
            <div className={styles.uploadSection}>
              <div className={styles.instructions}>
                <h3>הוראות:</h3>
                <ul>
                  <li>קובץ CSV או Excel חייב להכיל שורת כותרות</li>
                  <li>חובה: עמודות תיאור וסכום</li>
                  <li>אופציונלי: תאריך, קטגוריה, ספק, מספר חשבונית, תשלום ל, אמצעי תשלום</li>
                  <li>קטגוריות מותרות: תחזוקה שוטפת, תיקונים, חשבונות, ביטוח, שיפוצים, ניקיון, אחר</li>
                  <li>אמצעי תשלום מותרים: מזומן, צ'ק, העברה בנקאית, ביט</li>
                  <li>תאריך ריק יוגדר כתאריך היום</li>
                  <li>קטגוריה ריקה תוגדר כ"תחזוקה שוטפת"</li>
                  <li>אמצעי תשלום ריק יוגדר כ"מזומן"</li>
                  <li>מומלץ: השתמש בקבצי CSV לתאימות מלאה</li>
                </ul>
              </div>

              <div className={styles.uploadArea}>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className={styles.fileInput}
                  id="expense-excel-upload"
                />
                <label htmlFor="expense-excel-upload" className={styles.uploadLabel}>
                  <Upload size={48} />
                  <span>לחץ לבחירת קובץ CSV או Excel</span>
                  <small>קבצים נתמכים: .csv, .xlsx, .xls</small>
                </label>
              </div>

              <div className={styles.templateSection}>
                <button className={styles.templateButton} onClick={downloadTemplate}>
                  <Download size={16} />
                  הורד תבנית CSV לדוגמה
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Header Row Selection */}
        {currentStep === 2 && (
          <div className={styles.content}>
            <div className={styles.headerSelection}>
              <h3>בחר שורת כותרות</h3>
              <div className={styles.fileInfo}>
                <strong>קובץ:</strong> {file?.name} | <strong>סה"כ שורות:</strong> {rawData.length}
              </div>

              <div className={styles.headerRowSelector}>
                <label>שורת כותרות (מתחילה מ-1):</label>
                <select
                  value={headerRowIndex + 1}
                  onChange={(e) => handleHeaderRowChange(parseInt(e.target.value) - 1)}
                  className={styles.columnSelect}
                >
                  {rawData.map((_, index) => (
                    <option key={index} value={index + 1}>
                      שורה {index + 1}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview of selected header row and data */}
              <div className={styles.headerPreview}>
                <h4>תצוגה מקדימה של הקובץ:</h4>
                <div className={styles.previewContainer}>
                  <div className={styles.previewTable}>
                    <table>
                      <thead>
                        <tr>
                          <th>תיאור</th><th>סכום</th><th>תאריך</th><th>קטגוריה</th><th>ספק</th><th>מספר חשבונית</th><th>תשלום ל</th><th>אמצעי תשלום</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excelData.slice(0, 3).map((row, index) => {
                          // Find indices for each field based on mapping
                          const descriptionIndex = columnMapping.description ?
                            headers.findIndex(h => h === columnMapping.description) : -1;
                          const amountIndex = columnMapping.amount ?
                            headers.findIndex(h => h === columnMapping.amount) : -1;
                          const dateIndex = columnMapping.date ?
                            headers.findIndex(h => h === columnMapping.date) : -1;
                          const categoryIndex = columnMapping.category ?
                            headers.findIndex(h => h === columnMapping.category) : -1;
                          const vendorIndex = columnMapping.vendor ?
                            headers.findIndex(h => h === columnMapping.vendor) : -1;
                          const invoiceNumberIndex = columnMapping.invoiceNumber ?
                            headers.findIndex(h => h === columnMapping.invoiceNumber) : -1;
                          const paidToIndex = columnMapping.paidTo ?
                            headers.findIndex(h => h === columnMapping.paidTo) : -1;
                          const paymentMethodIndex = columnMapping.paymentMethod ?
                            headers.findIndex(h => h === columnMapping.paymentMethod) : -1;

                          return (
                            <tr key={index}>
                              <td>{descriptionIndex >= 0 ? row[descriptionIndex] || 'חסר' : 'לא נבחר'}</td><td>{amountIndex >= 0 ? row[amountIndex] || 'חסר' : 'לא נבחר'}</td><td>{dateIndex >= 0 ? row[dateIndex] || 'תאריך היום' : 'לא נבחר'}</td><td>{categoryIndex >= 0 ? row[categoryIndex] || 'תחזוקה שוטפת (ברירת מחדל)' : 'לא נבחר'}</td><td>{vendorIndex >= 0 ? row[vendorIndex] || 'ריק' : 'לא נבחר'}</td><td>{invoiceNumberIndex >= 0 ? row[invoiceNumberIndex] || 'ריק' : 'לא נבחר'}</td><td>{paidToIndex >= 0 ? row[paidToIndex] || 'ריק' : 'לא נבחר'}</td><td>{paymentMethodIndex >= 0 ? row[paymentMethodIndex] || 'מזומן (ברירת מחדל)' : 'לא נבחר'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {headers.length > 0 && (
                  <div className={styles.selectedHeaders}>
                    <h4>כותרות שנבחרו:</h4>
                    <div className={styles.headersList}>
                      {headers.map((header, index) => (
                        <span key={index} className={styles.headerTag}>
                          {header || `עמודה ${index + 1}`}
                        </span>
                      ))}
                    </div>
                    <p className={styles.dataInfo}>
                      <strong>שורות נתונים זמינות:</strong> {excelData.length}
                    </p>
                  </div>
                )}
              </div>

              <div className={styles.actions}>
                <button className={styles.backButton} onClick={resetUpload}>
                  חזור
                </button>
                <button
                  className={styles.processButton}
                  onClick={() => setCurrentStep(3)}
                  disabled={headers.length === 0 || excelData.length === 0}
                >
                  המשך להתאמת עמודות
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Column Mapping */}
        {currentStep === 3 && (
          <div className={styles.content}>
            <div className={styles.mappingSection}>
              <h3>בחר התאמת עמודות</h3>
              <div className={styles.fileInfo}>
                <strong>קובץ:</strong> {file?.name} |
                <strong> שורת כותרות:</strong> {headerRowIndex + 1} |
                <strong> שורות נתונים:</strong> {excelData.length}
              </div>

              <div className={styles.mappingGrid}>
                <div className={styles.mappingRow}>
                  <label>תאריך (אופציונלי):</label>
                  <select
                    value={columnMapping.date}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, date: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label className={styles.required}>תיאור (חובה):</label>
                  <select
                    value={columnMapping.description}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, description: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label className={styles.required}>סכום (חובה):</label>
                  <select
                    value={columnMapping.amount}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, amount: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>קטגוריה (אופציונלי):</label>
                  <select
                    value={columnMapping.category}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, category: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>ספק (אופציונלי):</label>
                  <select
                    value={columnMapping.vendor}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, vendor: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>מספר חשבונית (אופציונלי):</label>
                  <select
                    value={columnMapping.invoiceNumber}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>תשלום ל (אופציונלי):</label>
                  <select
                    value={columnMapping.paidTo}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, paidTo: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                {/* NEW: Payment Method Mapping */}
                <div className={styles.mappingRow}>
                  <label>אמצעי תשלום (אופציונלי):</label>
                  <select
                    value={columnMapping.paymentMethod}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className={styles.preview}>
                <h4>תצוגה מקדימה (3 שורות ראשונות):</h4>
                // Fix for the table in the preview section (around line 715)
                // Replace the existing table structure with this cleaner version:

                <div className={styles.previewTable}>
                  <table>
                    <thead>
                      <tr>{[
                        'תיאור',
                        'סכום',
                        'תאריך',
                        'קטגוריה',
                        'ספק',
                        'מספר חשבונית',
                        'תשלום ל',
                        'אמצעי תשלום'
                      ].map((header, idx) => <th key={idx}>{header}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.slice(0, 3).map((row, index) => {
                        // Find indices for each field based on mapping
                        const descriptionIndex = columnMapping.description ?
                          headers.findIndex(h => h === columnMapping.description) : -1;
                        const amountIndex = columnMapping.amount ?
                          headers.findIndex(h => h === columnMapping.amount) : -1;
                        const dateIndex = columnMapping.date ?
                          headers.findIndex(h => h === columnMapping.date) : -1;
                        const categoryIndex = columnMapping.category ?
                          headers.findIndex(h => h === columnMapping.category) : -1;
                        const vendorIndex = columnMapping.vendor ?
                          headers.findIndex(h => h === columnMapping.vendor) : -1;
                        const invoiceNumberIndex = columnMapping.invoiceNumber ?
                          headers.findIndex(h => h === columnMapping.invoiceNumber) : -1;
                        const paidToIndex = columnMapping.paidTo ?
                          headers.findIndex(h => h === columnMapping.paidTo) : -1;
                        const paymentMethodIndex = columnMapping.paymentMethod ?
                          headers.findIndex(h => h === columnMapping.paymentMethod) : -1;

                        const cells = [
                          descriptionIndex >= 0 ? row[descriptionIndex] || 'חסר' : 'לא נבחר',
                          amountIndex >= 0 ? row[amountIndex] || 'חסר' : 'לא נבחר',
                          dateIndex >= 0 ? row[dateIndex] || 'תאריך היום' : 'לא נבחר',
                          categoryIndex >= 0 ? row[categoryIndex] || 'תחזוקה שוטפת (ברירת מחדל)' : 'לא נבחר',
                          vendorIndex >= 0 ? row[vendorIndex] || 'ריק' : 'לא נבחר',
                          invoiceNumberIndex >= 0 ? row[invoiceNumberIndex] || 'ריק' : 'לא נבחר',
                          paidToIndex >= 0 ? row[paidToIndex] || 'ריק' : 'לא נבחר',
                          paymentMethodIndex >= 0 ? row[paymentMethodIndex] || 'מזומן (ברירת מחדל)' : 'לא נבחר'
                        ];

                        return (
                          <tr key={index}>{cells.map((cell, cellIdx) => <td key={cellIdx}>{cell}</td>)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button className={styles.backButton} onClick={() => setCurrentStep(2)}>
                חזור
              </button>
              <button
                className={styles.processButton}
                onClick={processExpenses}
                disabled={!columnMapping.description || !columnMapping.amount}
              >
                <Calculator size={16} />
                יצירת {excelData.length} הוצאות
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Processing */}
        {currentStep === 4 && (
          <div className={styles.content}>
            <div className={styles.processing}>
              <div className={styles.progressSection}>
                <h3>יוצר הוצאות...</h3>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>
                <div className={styles.progressText}>
                  {progress.current} מתוך {progress.total} הוצאות
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Results */}
        {currentStep === 5 && (
          <div className={styles.content}>
            <div className={styles.results}>
              <h3>תוצאות העלאה</h3>

              <div className={styles.resultsSummary}>
                <div className={`${styles.summaryCard} ${styles.success}`}>
                  <CheckCircle size={20} />
                  <span>נוצרו בהצלחה: {results.success.length}</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.error}`}>
                  <XCircle size={20} />
                  <span>שגיאות: {results.errors.length}</span>
                </div>
              </div>

              {/* Always show success results section */}
              <div className={styles.successResults}>
                <h4>
                  <CheckCircle size={16} />
                  הוצאות שנוצרו בהצלחה ({results.success.length})
                </h4>
                <div className={styles.resultsList}>
                  {results.success.length > 0 ? (
                    results.success.map((result, index) => (
                      <div key={index} className={styles.resultItem}>
                        <span>{result.description} - {result.amount.toLocaleString()} ₪</span>
                        <small>
                          קטגוריה: {result.category}
                          {result.vendor !== 'לא צוין' && `, ספק: ${result.vendor}`}
                          {result.paymentMethod && `, תשלום: ${result.paymentMethod}`} {/* NEW */}
                        </small>
                      </div>
                    ))
                  ) : (
                    <div className={styles.resultItem}>
                      <span style={{ color: '#999', fontStyle: 'italic' }}>לא נוצרו הוצאות</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Always show errors section */}
              <div className={styles.errorResults}>
                <h4>
                  <AlertCircle size={16} />
                  שגיאות ({results.errors.length})
                </h4>
                <div className={styles.resultsList}>
                  {results.errors.length > 0 ? (
                    results.errors.map((error, index) => (
                      <div key={index} className={styles.resultItem}>
                        <span>שורה {error.row}: {error.description} - {error.amount}</span>
                        <small className={styles.errorMessage}>{error.error}</small>
                      </div>
                    ))
                  ) : (
                    <div className={styles.resultItem}>
                      <span style={{ color: '#28a745', fontStyle: 'italic' }}>אין שגיאות - כל ההוצאות נוצרו בהצלחה!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button className={styles.backButton} onClick={resetUpload}>
                העלאה חדשה
              </button>
              <button className={styles.closeButton} onClick={onClose}>
                סגור
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseExcelUpload;